import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { SEO } from "@/components/seo";
import type { CarouselSlide, Product, Category, Order, OrderItem, OrderMessage } from "@shared/schema";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, Loader2, ShieldAlert, X, FileIcon, ShoppingCart, Package, Eye, Mail, CreditCard, Zap, HelpCircle, CheckCircle2, ArrowRight, Check } from "lucide-react";

type AdminOrderItem = OrderItem & {
  product: { id: number; name: string; slug: string; image: string | null; price: string } | null;
};

type EmailTemplate = {
  key: string;
  name: string;
  description: string;
  subject: string;
  body: string;
  variables: string | null;
  updatedAt: string | null;
};

type SubscriptionPlan = {
  id: number;
  name: string;
  description: string;
  price: string;
  tier: string;
  stripePriceId: string | null;
  stripeProductId: string | null;
  features?: string[] | null;
  downloadFiles: string[] | null;
  isActive: boolean;
  sortOrder: number;
};

type PlanForm = {
  name: string;
  description: string;
  price: string;
  tier: string;
  features: string[];
  downloadFiles: Array<{ url: string; name: string }>;
  isActive: boolean;
  sortOrder: number;
};

const defaultPlanForm: PlanForm = {
  name: "",
  description: "",
  price: "",
  tier: "indicators",
  features: [],
  downloadFiles: [],
  isActive: true,
  sortOrder: 0,
};

type AdminOrder = Order & {
  items: AdminOrderItem[];
  isSubscription?: boolean;
  subscriptionId?: string | null;
  subscriptionPlan?: { id: number; name: string; tier: string } | null;
  billing?: {
    paymentStatus: string;
    amountPaid: number;
    amountRefunded: number;
    refundableAmount: number;
    canRefund: boolean;
    canCancel: boolean;
    subscriptionStatus: string | null;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    stripeError?: string | null;
  };
  billingEvents?: Array<{ id: number | string; eventType: string; status: string; amount: number | null; details: string | null; createdAt: string }>;
};

type DiscountCode = {
  id: string;
  code: string;
  discountType: "percent" | "fixed";
  value: number | string;
  durationCount: number;
  durationUnit: "months" | "years";
  expiresAt: string | null;
  active: boolean;
};

type DiscountForm = {
  code: string;
  discountType: "percent" | "fixed";
  value: string;
  durationCount: string;
  durationUnit: "months" | "years";
  expiresAt: string;
};

type SubscriptionInvoice = {
  id: string;
  number: string | null;
  createdAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  paymentStatus: string;
  amountPaid: number;
  amountRefunded: number;
  refundableAmount: number;
  canRefund: boolean;
};

const defaultDiscountForm: DiscountForm = {
  code: "",
  discountType: "percent" as const,
  value: "",
  durationCount: "1",
  durationUnit: "months" as const,
  expiresAt: "",
};

type SlideForm = {
  badge: string;
  title: string;
  highlight: string;
  description: string;
  image: string;
  primaryLink: string;
  primaryLabel: string;
  secondaryLink: string;
  secondaryLabel: string;
  sortOrder: number;
  isActive: boolean;
};

type DownloadFileEntry = {
  url: string;
  name: string;
};

type ProductForm = {
  name: string;
  slug: string;
  description: string;
  price: string;
  categoryId: number;
  image: string;
  images: string[];
  downloadFile: string;
  downloadFiles: DownloadFileEntry[];
  instructions: string;
  instructionImages: string[];
  features: string;
  howToUse: string;
  isFeatured: boolean;
  sortOrder: number;
  saleType: string;
  subscriptionPlanId: number | null;
};

const defaultSlideForm: SlideForm = {
  badge: "",
  title: "",
  highlight: "",
  description: "",
  image: "",
  primaryLink: "",
  primaryLabel: "",
  secondaryLink: "",
  secondaryLabel: "",
  sortOrder: 0,
  isActive: true,
};

type CategoryForm = {
  name: string;
  slug: string;
  description: string;
  image: string;
  group: string;
  sortOrder: number;
};

const defaultCategoryForm: CategoryForm = {
  name: "",
  slug: "",
  description: "",
  image: "",
  group: "templates",
  sortOrder: 0,
};

const defaultProductForm: ProductForm = {
  name: "",
  slug: "",
  description: "",
  price: "",
  categoryId: 0,
  image: "",
  images: [],
  downloadFile: "",
  downloadFiles: [],
  instructions: "",
  instructionImages: [],
  features: "",
  howToUse: "",
  isFeatured: false,
  sortOrder: 0,
  saleType: "purchase",
  subscriptionPlanId: null,
};

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export default function AdminPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [slideDialogOpen, setSlideDialogOpen] = useState(false);
  const [editingSlide, setEditingSlide] = useState<CarouselSlide | null>(null);
  const [slideForm, setSlideForm] = useState<SlideForm>(defaultSlideForm);

  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState<ProductForm>(defaultProductForm);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryForm>(defaultCategoryForm);

  const slideFileRef = useRef<HTMLInputElement>(null);
  const productFileRef = useRef<HTMLInputElement>(null);
  const downloadFileRef = useRef<HTMLInputElement>(null);
  const categoryFileRef = useRef<HTMLInputElement>(null);
  const planFileRef = useRef<HTMLInputElement>(null);

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [planForm, setPlanForm] = useState<PlanForm>(defaultPlanForm);
  const [featureInput, setFeatureInput] = useState("");

  const { data: slides, isLoading: slidesLoading } = useQuery<CarouselSlide[]>({
    queryKey: ["/api/admin/carousel"],
    enabled: !!user?.isAdmin,
  });

  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/admin/products"],
    enabled: !!user?.isAdmin,
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ["/api/admin/categories"],
    enabled: !!user?.isAdmin,
  });

  const { data: adminOrders, isLoading: ordersLoading } = useQuery<AdminOrder[]>({
    queryKey: ["/api/admin/orders"],
    enabled: !!user?.isAdmin,
  });

  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [adminReplyBody, setAdminReplyBody] = useState("");
  const [refundOrder, setRefundOrder] = useState<AdminOrder | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refundInvoiceId, setRefundInvoiceId] = useState("");
  const [cancelOrder, setCancelOrder] = useState<AdminOrder | null>(null);
  const [cancelMode, setCancelMode] = useState<"period_end" | "immediate">("period_end");
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState<DiscountForm>(defaultDiscountForm);

  const [editingTemplateKey, setEditingTemplateKey] = useState<string | null>(null);
  const [templateEditForm, setTemplateEditForm] = useState({ subject: "", body: "" });

  const [disclaimerEdit, setDisclaimerEdit] = useState<string | null>(null);

  const { data: adminUnreadCounts = {} as Record<number, number> } = useQuery<Record<number, number>>({
    queryKey: ["/api/admin/messages/unread-counts"],
    enabled: !!user?.isAdmin,
    refetchInterval: 20000,
  });

  const { data: emailTemplatesList, isLoading: emailTemplatesLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/admin/email-templates"],
    enabled: !!user?.isAdmin,
  });

  const { data: siteSettings } = useQuery<Record<string, string>>({
    queryKey: ["/api/site/settings"],
    enabled: !!user?.isAdmin,
  });

  const { data: subscriptionPlans, isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/admin/subscription-plans"],
    enabled: !!user?.isAdmin,
  });

  const { data: discountCodes = [], isLoading: discountCodesLoading } = useQuery<DiscountCode[]>({
    queryKey: ["/api/admin/discount-codes"],
    enabled: !!user?.isAdmin,
  });

  const { data: refundInvoices = [], isLoading: refundInvoicesLoading } = useQuery<SubscriptionInvoice[]>({
    queryKey: ["/api/admin/orders", refundOrder?.id, "subscription-invoices"],
    enabled: !!refundOrder?.isSubscription,
  });

  const { data: expandedOrderMessages = [] } = useQuery<OrderMessage[]>({
    queryKey: ["/api/admin/orders", expandedOrderId, "messages"],
    queryFn: async () => {
      if (!expandedOrderId) return [];
      const res = await apiRequest("GET", `/api/admin/orders/${expandedOrderId}/messages`);
      return res.json();
    },
    enabled: !!expandedOrderId,
    refetchInterval: expandedOrderId ? 10000 : false,
  });

  const sendAdminReplyMutation = useMutation({
    mutationFn: async ({ orderId, body }: { orderId: number; body: string }) => {
      const res = await apiRequest("POST", `/api/admin/orders/${orderId}/message`, { body });
      return res.json();
    },
    onSuccess: () => {
      setAdminReplyBody("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders", expandedOrderId, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/messages/unread-counts"] });
      toast({ title: "Message sent" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to send message", description: error.message, variant: "destructive" });
    },
  });

  const refundMutation = useMutation({
    mutationFn: async ({ orderId, amount, reason, invoiceId }: { orderId: number; amount?: number; reason?: string; invoiceId?: string }) => {
      const res = await apiRequest("POST", `/api/admin/orders/${orderId}/refund`, {
        ...(amount !== undefined ? { amount } : {}),
        ...(reason ? { reason } : {}),
        ...(invoiceId ? { invoiceId } : {}),
        requestId: crypto.randomUUID(),
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      setRefundOrder(null);
      setRefundAmount("");
      setRefundReason("");
      setRefundInvoiceId("");
      toast({
        title: data.reconciliationPending ? "Refund issued; cancellation pending" : "Refund submitted",
        description: data.reconciliationPending
          ? "The refund succeeded. Subscription cancellation will retry automatically."
          : undefined,
      });
    },
    onError: (error: Error) => toast({ title: "Refund failed", description: error.message, variant: "destructive" }),
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async ({ orderId, mode }: { orderId: number; mode: "period_end" | "immediate" }) => {
      const res = await apiRequest("POST", `/api/admin/orders/${orderId}/cancel-subscription`, {
        mode,
        requestId: crypto.randomUUID(),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      setCancelOrder(null);
      toast({ title: "Subscription cancellation updated" });
    },
    onError: (error: Error) => toast({ title: "Cancellation failed", description: error.message, variant: "destructive" }),
  });

  const createDiscountCodeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/discount-codes", {
        code: discountForm.code.trim().toUpperCase(),
        discountType: discountForm.discountType,
        value: Number(discountForm.value),
        durationCount: Number(discountForm.durationCount),
        durationUnit: discountForm.durationUnit,
        expiresAt: discountForm.expiresAt ? new Date(`${discountForm.expiresAt}T23:59:59`).toISOString() : null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/discount-codes"] });
      setDiscountDialogOpen(false);
      setDiscountForm(defaultDiscountForm);
      toast({ title: "Discount code created" });
    },
    onError: (error: Error) => toast({ title: "Failed to create code", description: error.message, variant: "destructive" }),
  });

  const toggleDiscountCodeMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/discount-codes/${id}`, { active });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/discount-codes"] });
      toast({ title: "Discount code updated" });
    },
    onError: (error: Error) => toast({ title: "Failed to update code", description: error.message, variant: "destructive" }),
  });

  const saveSiteSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const res = await apiRequest("PUT", `/api/admin/site-settings/${key}`, { value });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/site/settings"] });
      setDisclaimerEdit(null);
      toast({ title: "Setting saved" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save setting", description: error.message, variant: "destructive" });
    },
  });

  const updateEmailTemplateMutation = useMutation({
    mutationFn: async ({ key, subject, body }: { key: string; subject: string; body: string }) => {
      const res = await apiRequest("PUT", `/api/admin/email-templates/${key}`, { subject, body });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-templates"] });
      toast({ title: "Template saved" });
      setEditingTemplateKey(null);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to save template", description: error.message, variant: "destructive" });
    },
  });

  const markAdminReadMutation = useMutation({
    mutationFn: async (orderId: number) => {
      const res = await apiRequest("POST", `/api/admin/orders/${orderId}/messages/read`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/messages/unread-counts"] });
    },
  });

  function handleExpandOrder(orderId: number) {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
    } else {
      setExpandedOrderId(orderId);
      if ((adminUnreadCounts as Record<number, number>)[orderId]) {
        markAdminReadMutation.mutate(orderId);
      }
    }
  }

  const totalAdminUnread = Object.values(adminUnreadCounts as Record<number, number>).reduce((s, n) => s + n, 0);

  const createSlideMutation = useMutation({
    mutationFn: async (data: SlideForm) => {
      const res = await apiRequest("POST", "/api/admin/carousel", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/carousel"] });
      toast({ title: "Slide created successfully" });
      setSlideDialogOpen(false);
      resetSlideForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create slide", description: error.message, variant: "destructive" });
    },
  });

  const updateSlideMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SlideForm }) => {
      const res = await apiRequest("PUT", `/api/admin/carousel/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/carousel"] });
      toast({ title: "Slide updated successfully" });
      setSlideDialogOpen(false);
      resetSlideForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update slide", description: error.message, variant: "destructive" });
    },
  });

  const deleteSlideMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/carousel/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/carousel"] });
      toast({ title: "Slide deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete slide", description: error.message, variant: "destructive" });
    },
  });

  const createPlanMutation = useMutation({
    mutationFn: async (data: PlanForm) => {
      const body = {
        ...data,
        downloadFiles: data.downloadFiles.length > 0 ? data.downloadFiles.map((f) => JSON.stringify(f)) : null,
      };
      const res = await apiRequest("POST", "/api/admin/subscription-plans", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/subscription-plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      toast({ title: "Plan created successfully" });
      setPlanDialogOpen(false);
      setPlanForm(defaultPlanForm);
      setEditingPlan(null);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create plan", description: error.message, variant: "destructive" });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PlanForm }) => {
      const body = {
        ...data,
        downloadFiles: data.downloadFiles.length > 0 ? data.downloadFiles.map((f) => JSON.stringify(f)) : null,
      };
      const res = await apiRequest("PUT", `/api/admin/subscription-plans/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/subscription-plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      toast({ title: "Plan updated successfully" });
      setPlanDialogOpen(false);
      setPlanForm(defaultPlanForm);
      setEditingPlan(null);
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update plan", description: error.message, variant: "destructive" });
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/subscription-plans/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/subscription-plans"] });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription-plans"] });
      toast({ title: "Plan deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete plan", description: error.message, variant: "destructive" });
    },
  });

  const createProductMutation = useMutation({
    mutationFn: async (data: ProductForm) => {
      const body = {
        ...data,
        images: data.images.length > 0 ? data.images : null,
        instructionImages: data.instructionImages.length > 0 ? data.instructionImages : null,
        downloadFiles: data.downloadFiles.length > 0 ? data.downloadFiles.map((f) => JSON.stringify(f)) : null,
        features: data.features ? data.features.split(",").map((f) => f.trim()).filter(Boolean) : [],
        howToUse: data.howToUse || null,
      };
      const res = await apiRequest("POST", "/api/admin/products", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/featured"] });
      toast({ title: "Product created successfully" });
      setProductDialogOpen(false);
      resetProductForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create product", description: error.message, variant: "destructive" });
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProductForm }) => {
      const body = {
        ...data,
        images: data.images.length > 0 ? data.images : null,
        instructionImages: data.instructionImages.length > 0 ? data.instructionImages : null,
        downloadFiles: data.downloadFiles.length > 0 ? data.downloadFiles.map((f) => JSON.stringify(f)) : null,
        features: data.features ? data.features.split(",").map((f) => f.trim()).filter(Boolean) : [],
        howToUse: data.howToUse || null,
      };
      const res = await apiRequest("PUT", `/api/admin/products/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/featured"] });
      toast({ title: "Product updated successfully" });
      setProductDialogOpen(false);
      resetProductForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update product", description: error.message, variant: "destructive" });
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/products/featured"] });
      toast({ title: "Product deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete product", description: error.message, variant: "destructive" });
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (data: CategoryForm) => {
      const res = await apiRequest("POST", "/api/admin/categories", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
      toast({ title: "Category created successfully" });
      setCategoryDialogOpen(false);
      resetCategoryForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to create category", description: error.message, variant: "destructive" });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CategoryForm }) => {
      const res = await apiRequest("PUT", `/api/admin/categories/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
      toast({ title: "Category updated successfully" });
      setCategoryDialogOpen(false);
      resetCategoryForm();
    },
    onError: (error: Error) => {
      toast({ title: "Failed to update category", description: error.message, variant: "destructive" });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/categories"] });
      toast({ title: "Category deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Failed to delete category", description: error.message, variant: "destructive" });
    },
  });

  function resetCategoryForm() {
    setEditingCategory(null);
    setCategoryForm(defaultCategoryForm);
  }

  function openEditCategory(cat: Category) {
    setEditingCategory(cat);
    setCategoryForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || "",
      image: cat.image || "",
      group: cat.group,
      sortOrder: cat.sortOrder ?? 0,
    });
    setCategoryDialogOpen(true);
  }

  function handleCategorySubmit() {
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data: categoryForm });
    } else {
      createCategoryMutation.mutate(categoryForm);
    }
  }

  function resetSlideForm() {
    setEditingSlide(null);
    setSlideForm(defaultSlideForm);
  }

  function resetProductForm() {
    setEditingProduct(null);
    setProductForm(defaultProductForm);
  }

  function openEditSlide(slide: CarouselSlide) {
    setEditingSlide(slide);
    setSlideForm({
      badge: slide.badge,
      title: slide.title,
      highlight: slide.highlight,
      description: slide.description,
      image: slide.image || "",
      primaryLink: slide.primaryLink,
      primaryLabel: slide.primaryLabel,
      secondaryLink: slide.secondaryLink,
      secondaryLabel: slide.secondaryLabel,
      sortOrder: slide.sortOrder,
      isActive: slide.isActive ?? true,
    });
    setSlideDialogOpen(true);
  }

  function openEditProduct(product: Product) {
    setEditingProduct(product);
    const rawFiles = product.downloadFiles || [];
    const parsedDownloadFiles: DownloadFileEntry[] = rawFiles.map((f) => {
      try {
        const parsed = JSON.parse(f);
        if (parsed && typeof parsed === "object" && parsed.url) return parsed;
      } catch {}
      return { url: f, name: f.split("/").pop() || "file" };
    });
    setProductForm({
      name: product.name,
      slug: product.slug,
      description: product.description,
      price: product.price,
      categoryId: product.categoryId,
      image: product.image || "",
      images: product.images || [],
      downloadFile: product.downloadFile || "",
      downloadFiles: parsedDownloadFiles,
      instructions: product.instructions || "",
      instructionImages: product.instructionImages || [],
      features: product.features ? product.features.join(", ") : "",
      howToUse: product.howToUse || "",
      isFeatured: product.isFeatured ?? false,
      sortOrder: product.sortOrder ?? 0,
      saleType: (product as any).saleType ?? "purchase",
      subscriptionPlanId: (product as any).subscriptionPlanId ?? null,
    });
    setProductDialogOpen(true);
  }

  function openEditPlan(plan: SubscriptionPlan) {
    setEditingPlan(plan);
    const rawFiles = plan.downloadFiles || [];
    const parsedFiles = rawFiles.map((f) => {
      try { const p = JSON.parse(f); if (p?.url) return p; } catch {}
      return { url: f, name: f.split("/").pop() || "file" };
    });
    setPlanForm({
      name: plan.name,
      description: plan.description,
      price: plan.price,
      tier: plan.tier,
      features: plan.features ?? [],
      downloadFiles: parsedFiles,
      isActive: plan.isActive,
      sortOrder: plan.sortOrder,
    });
    setFeatureInput("");
    setPlanDialogOpen(true);
  }

  async function handlePlanFileUpload(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/upload-file", { method: "POST", body: formData, credentials: "include" });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setPlanForm((prev) => ({
        ...prev,
        downloadFiles: [...prev.downloadFiles, { url: data.url, name: data.originalName || file.name }],
      }));
      toast({ title: "File uploaded successfully" });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    }
  }

  function handlePlanSubmit() {
    if (editingPlan) {
      updatePlanMutation.mutate({ id: editingPlan.id, data: planForm });
    } else {
      createPlanMutation.mutate(planForm);
    }
  }

  async function handleImageUpload(
    file: File,
    setImage: (url: string) => void
  ) {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setImage(data.url);
      toast({ title: "Image uploaded successfully" });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    }
  }

  async function handleFileUpload(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/upload-file", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setProductForm((prev) => ({
        ...prev,
        downloadFiles: [...prev.downloadFiles, { url: data.url, name: data.originalName || file.name }],
      }));
      toast({ title: "File uploaded successfully" });
    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    }
  }

  function handleSlideSubmit() {
    if (editingSlide) {
      updateSlideMutation.mutate({ id: editingSlide.id, data: slideForm });
    } else {
      createSlideMutation.mutate(slideForm);
    }
  }

  function handleProductSubmit() {
    if (editingProduct) {
      updateProductMutation.mutate({ id: editingProduct.id, data: productForm });
    } else {
      createProductMutation.mutate(productForm);
    }
  }

  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!user || !user.isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Unauthorized" description="Admin access required" noIndex />
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <ShieldAlert className="w-16 h-16 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Unauthorized</h1>
          <p className="text-muted-foreground">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  const slideMutating = createSlideMutation.isPending || updateSlideMutation.isPending;
  const productMutating = createProductMutation.isPending || updateProductMutation.isPending;
  const categoryMutating = createCategoryMutation.isPending || updateCategoryMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Admin Panel" description="Manage carousel slides and products" noIndex />
      <h1 className="text-2xl sm:text-3xl font-bold mb-6" data-testid="text-admin-title">
        Admin Panel
      </h1>

      <Tabs defaultValue="carousel">
        <TabsList>
          <TabsTrigger value="carousel" data-testid="tab-carousel">
            Carousel
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            Products
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            Categories
          </TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases" className="relative">
            Purchases
            {totalAdminUnread > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {totalAdminUnread > 9 ? "9+" : totalAdminUnread}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">
            Emails
          </TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">
            Subscriptions
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="carousel" className="mt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <h2 className="text-xl font-semibold">Carousel Slides</h2>
            <Dialog
              open={slideDialogOpen}
              onOpenChange={(open) => {
                setSlideDialogOpen(open);
                if (!open) resetSlideForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-slide"
                  onClick={() => {
                    resetSlideForm();
                    setSlideDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Slide
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingSlide ? "Edit Slide" : "Add New Slide"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Badge</Label>
                    <Input
                      value={slideForm.badge}
                      onChange={(e) => setSlideForm({ ...slideForm, badge: e.target.value })}
                      data-testid="input-slide-badge"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={slideForm.title}
                      onChange={(e) => setSlideForm({ ...slideForm, title: e.target.value })}
                      data-testid="input-slide-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Highlight</Label>
                    <Input
                      value={slideForm.highlight}
                      onChange={(e) => setSlideForm({ ...slideForm, highlight: e.target.value })}
                      data-testid="input-slide-highlight"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={slideForm.description}
                      onChange={(e) => setSlideForm({ ...slideForm, description: e.target.value })}
                      data-testid="input-slide-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Image</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={slideForm.image}
                        onChange={(e) => setSlideForm({ ...slideForm, image: e.target.value })}
                        placeholder="Image URL"
                        className="flex-1"
                        data-testid="input-slide-image"
                      />
                      <input
                        ref={slideFileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, (url) =>
                              setSlideForm((prev) => ({ ...prev, image: url }))
                            );
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => slideFileRef.current?.click()}
                        data-testid="button-upload-slide-image"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Link</Label>
                      <Input
                        value={slideForm.primaryLink}
                        onChange={(e) => setSlideForm({ ...slideForm, primaryLink: e.target.value })}
                        data-testid="input-slide-primary-link"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Label</Label>
                      <Input
                        value={slideForm.primaryLabel}
                        onChange={(e) => setSlideForm({ ...slideForm, primaryLabel: e.target.value })}
                        data-testid="input-slide-primary-label"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Link</Label>
                      <Input
                        value={slideForm.secondaryLink}
                        onChange={(e) => setSlideForm({ ...slideForm, secondaryLink: e.target.value })}
                        data-testid="input-slide-secondary-link"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Label</Label>
                      <Input
                        value={slideForm.secondaryLabel}
                        onChange={(e) => setSlideForm({ ...slideForm, secondaryLabel: e.target.value })}
                        data-testid="input-slide-secondary-label"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sort Order</Label>
                      <Input
                        type="number"
                        value={slideForm.sortOrder}
                        onChange={(e) =>
                          setSlideForm({ ...slideForm, sortOrder: parseInt(e.target.value) || 0 })
                        }
                        data-testid="input-slide-sort-order"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <Switch
                        checked={slideForm.isActive}
                        onCheckedChange={(checked) =>
                          setSlideForm({ ...slideForm, isActive: checked })
                        }
                        data-testid="switch-slide-active"
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-4 flex-wrap">
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                      onClick={handleSlideSubmit}
                      disabled={slideMutating}
                      data-testid="button-save-slide"
                    >
                      {slideMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingSlide ? "Update Slide" : "Create Slide"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {slidesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : slides && slides.length > 0 ? (
            <div className="space-y-4">
              {slides.map((slide) => (
                <Card key={slide.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="no-default-active-elevate">
                            {slide.badge}
                          </Badge>
                          <Badge
                            variant={slide.isActive ? "default" : "outline"}
                            className="no-default-active-elevate"
                          >
                            {slide.isActive ? "Active" : "Inactive"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            Order: {slide.sortOrder}
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg">{slide.title}</h3>
                        <p className="text-sm text-muted-foreground">{slide.highlight}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {slide.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditSlide(slide)}
                          data-testid={`button-edit-slide-${slide.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this slide?")) {
                              deleteSlideMutation.mutate(slide.id);
                            }
                          }}
                          data-testid={`button-delete-slide-${slide.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No carousel slides found. Add one to get started.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <h2 className="text-xl font-semibold">Products</h2>
            <Dialog
              open={productDialogOpen}
              onOpenChange={(open) => {
                setProductDialogOpen(open);
                if (!open) resetProductForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-product"
                  onClick={() => {
                    resetProductForm();
                    setProductDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingProduct ? "Edit Product" : "Add New Product"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={productForm.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setProductForm({
                          ...productForm,
                          name,
                          slug: generateSlug(name),
                        });
                      }}
                      data-testid="input-product-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      value={productForm.slug}
                      onChange={(e) => setProductForm({ ...productForm, slug: e.target.value })}
                      data-testid="input-product-slug"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      data-testid="input-product-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <Input
                        value={productForm.price}
                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                        data-testid="input-product-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={productForm.categoryId ? String(productForm.categoryId) : ""}
                        onValueChange={(val) =>
                          setProductForm({ ...productForm, categoryId: parseInt(val) })
                        }
                      >
                        <SelectTrigger data-testid="select-product-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories?.map((cat) => (
                            <SelectItem key={cat.id} value={String(cat.id)}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Main Image</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={productForm.image}
                        onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                        placeholder="Image URL"
                        className="flex-1"
                        data-testid="input-product-image"
                      />
                      <input
                        ref={productFileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, (url) =>
                              setProductForm((prev) => ({ ...prev, image: url }))
                            );
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => productFileRef.current?.click()}
                        data-testid="button-upload-product-image"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Additional Images</Label>
                    <div className="flex flex-wrap gap-2">
                      {productForm.images.map((img, idx) => (
                        <div key={idx} className="relative w-20 h-20 rounded-md overflow-visible border">
                          <img src={img} alt={`Product ${idx + 1}`} className="w-full h-full object-cover rounded-md" />
                          <div className="absolute -top-2 -right-2">
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="!h-5 !w-5 !min-h-0 rounded-full"
                              onClick={() => {
                                setProductForm((prev) => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== idx),
                                }));
                              }}
                              data-testid={`button-remove-image-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="product-additional-images"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, (url) =>
                              setProductForm((prev) => ({
                                ...prev,
                                images: [...prev.images, url],
                              }))
                            );
                          }
                          e.target.value = "";
                        }}
                      />
                      <div
                        className="w-20 h-20 rounded-md border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover-elevate active-elevate-2 transition-colors"
                        onClick={() => document.getElementById("product-additional-images")?.click()}
                        data-testid="button-upload-additional-image"
                      >
                        <Plus className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Upload additional product images. Click the X to remove.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Download Files</Label>
                    <div className="space-y-2">
                      {productForm.downloadFiles.map((df, idx) => (
                        <div key={idx} className="flex items-center gap-2" data-testid={`download-file-entry-${idx}`}>
                          <div className="flex-1 min-w-0 flex items-center gap-2 p-2 rounded-md bg-muted/50">
                            <FileIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                            <span className="text-sm truncate">{df.name}</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              setProductForm((prev) => ({
                                ...prev,
                                downloadFiles: prev.downloadFiles.filter((_, i) => i !== idx),
                              }));
                            }}
                            data-testid={`button-remove-download-file-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <input
                        ref={downloadFileRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => downloadFileRef.current?.click()}
                        data-testid="button-upload-download-file"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload File
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Upload files that customers will download after purchase. You can upload multiple files.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Instructions</Label>
                    <Textarea
                      value={productForm.instructions}
                      onChange={(e) => setProductForm({ ...productForm, instructions: e.target.value })}
                      placeholder="Enter instructions for how to use this product after download (e.g., installation steps, configuration tips)..."
                      className="min-h-[120px]"
                      data-testid="input-product-instructions"
                    />
                    <p className="text-xs text-muted-foreground">These instructions will be shown to customers after they purchase this product.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Instruction Images</Label>
                    <div className="flex flex-wrap gap-2">
                      {productForm.instructionImages.map((img, idx) => (
                        <div key={idx} className="relative w-20 h-20 rounded-md overflow-visible border">
                          <img src={img} alt={`Instruction ${idx + 1}`} className="w-full h-full object-cover rounded-md" />
                          <div className="absolute -top-2 -right-2">
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="!h-5 !w-5 !min-h-0 rounded-full"
                              onClick={() => {
                                setProductForm((prev) => ({
                                  ...prev,
                                  instructionImages: prev.instructionImages.filter((_, i) => i !== idx),
                                }));
                              }}
                              data-testid={`button-remove-instruction-image-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="product-instruction-images"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, (url) =>
                              setProductForm((prev) => ({
                                ...prev,
                                instructionImages: [...prev.instructionImages, url],
                              }))
                            );
                          }
                          e.target.value = "";
                        }}
                      />
                      <div
                        className="w-20 h-20 rounded-md border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover-elevate active-elevate-2 transition-colors"
                        onClick={() => document.getElementById("product-instruction-images")?.click()}
                        data-testid="button-upload-instruction-image"
                      >
                        <Plus className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Upload images to show alongside instructions after purchase (e.g., screenshots of setup steps).</p>
                  </div>
                  <div className="space-y-2">
                    <Label>How to Use</Label>
                    <Textarea
                      value={productForm.howToUse}
                      onChange={(e) => setProductForm({ ...productForm, howToUse: e.target.value })}
                      placeholder="Explain how customers should use this product once they've installed it (tips, best practices, workflow advice)..."
                      className="min-h-[120px]"
                      data-testid="input-product-how-to-use"
                    />
                    <p className="text-xs text-muted-foreground">Shown to customers after purchase, below the installation instructions.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Features (comma-separated)</Label>
                    <Textarea
                      value={productForm.features}
                      onChange={(e) => setProductForm({ ...productForm, features: e.target.value })}
                      placeholder="Feature 1, Feature 2, Feature 3"
                      data-testid="input-product-features"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sale Type</Label>
                    <Select
                      value={productForm.saleType}
                      onValueChange={(val) => setProductForm({ ...productForm, saleType: val })}
                    >
                      <SelectTrigger data-testid="select-product-sale-type">
                        <SelectValue placeholder="Select sale type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="purchase">Purchase Only — individual buy</SelectItem>
                        <SelectItem value="subscribe">Subscribe Only — subscription required</SelectItem>
                        <SelectItem value="both">Both — purchase or subscribe</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Controls how customers can acquire this product.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Subscription Plan</Label>
                    <Select
                      value={productForm.subscriptionPlanId != null ? String(productForm.subscriptionPlanId) : "none"}
                      onValueChange={(val) => setProductForm({ ...productForm, subscriptionPlanId: val === "none" ? null : Number(val) })}
                    >
                      <SelectTrigger data-testid="select-product-plan">
                        <SelectValue placeholder="Not assigned to a plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Not in any plan —</SelectItem>
                        {(subscriptionPlans ?? []).map((plan) => (
                          <SelectItem key={plan.id} value={String(plan.id)}>{plan.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">The subscription plan this product is listed under.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sort Order</Label>
                      <Input
                        type="number"
                        value={productForm.sortOrder}
                        onChange={(e) => setProductForm({ ...productForm, sortOrder: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        data-testid="input-product-sort-order"
                      />
                      <p className="text-xs text-muted-foreground">Lower numbers appear first</p>
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <Switch
                        checked={productForm.isFeatured}
                        onCheckedChange={(checked) =>
                          setProductForm({ ...productForm, isFeatured: checked })
                        }
                        data-testid="switch-product-featured"
                      />
                      <Label>Featured</Label>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-4 flex-wrap">
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                      onClick={handleProductSubmit}
                      disabled={productMutating}
                      data-testid="button-save-product"
                    >
                      {productMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingProduct ? "Update Product" : "Create Product"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {productsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : products && products.length > 0 ? (
            <div className="space-y-3">
              {products.map((product) => {
                const category = categories?.find((c) => c.id === product.categoryId);
                return (
                  <Card key={product.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4 flex-1 min-w-0 flex-wrap">
                          {product.image && (
                            <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold">{product.name}</h3>
                              {product.isFeatured && (
                                <Badge variant="secondary" className="no-default-active-elevate">
                                  Featured
                                </Badge>
                              )}
                              {(() => {
                                const st = (product as any).saleType ?? "subscribe";
                                if (st === "purchase") return <Badge variant="outline" className="no-default-active-elevate text-xs">Purchase</Badge>;
                                if (st === "subscribe") return <Badge variant="outline" className="no-default-active-elevate text-xs border-primary/40 text-primary">Subscribe</Badge>;
                                if (st === "both") return <Badge variant="outline" className="no-default-active-elevate text-xs border-green-500/40 text-green-600">Both</Badge>;
                                return null;
                              })()}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {product.slug} &middot; ${product.price}
                              {category ? ` · ${category.name}` : ""}
                              {product.sortOrder > 0 ? ` · Order: ${product.sortOrder}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openEditProduct(product)}
                            data-testid={`button-edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this product?")) {
                                deleteProductMutation.mutate(product.id);
                              }
                            }}
                            data-testid={`button-delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No products found. Add one to get started.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <h2 className="text-xl font-semibold">Categories</h2>
            <Dialog
              open={categoryDialogOpen}
              onOpenChange={(open) => {
                setCategoryDialogOpen(open);
                if (!open) resetCategoryForm();
              }}
            >
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-category"
                  onClick={() => {
                    resetCategoryForm();
                    setCategoryDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Category
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingCategory ? "Edit Category" : "Add New Category"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setCategoryForm({
                          ...categoryForm,
                          name,
                          slug: generateSlug(name),
                        });
                      }}
                      data-testid="input-category-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      value={categoryForm.slug}
                      onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                      data-testid="input-category-slug"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={categoryForm.description}
                      onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                      data-testid="input-category-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Group</Label>
                    <Select
                      value={categoryForm.group}
                      onValueChange={(val) => setCategoryForm({ ...categoryForm, group: val })}
                    >
                      <SelectTrigger data-testid="select-category-group">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="templates">Templates</SelectItem>
                        <SelectItem value="indicators">Indicators</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={categoryForm.sortOrder}
                      onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                      data-testid="input-category-sort-order"
                    />
                    <p className="text-xs text-muted-foreground">Lower numbers appear first</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Image</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={categoryForm.image}
                        onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                        placeholder="Image URL"
                        className="flex-1"
                        data-testid="input-category-image"
                      />
                      <input
                        ref={categoryFileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, (url) =>
                              setCategoryForm((prev) => ({ ...prev, image: url }))
                            );
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => categoryFileRef.current?.click()}
                        data-testid="button-upload-category-image"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-4 flex-wrap">
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                      onClick={handleCategorySubmit}
                      disabled={categoryMutating}
                      data-testid="button-save-category"
                    >
                      {categoryMutating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingCategory ? "Update Category" : "Create Category"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {categories && categories.length > 0 ? (
            <div className="space-y-3">
              {categories.map((cat) => (
                <Card key={cat.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-4 flex-1 min-w-0 flex-wrap">
                        {cat.image && (
                          <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                            <img
                              src={cat.image}
                              alt={cat.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{cat.name}</h3>
                            <Badge variant="secondary" className="no-default-active-elevate">
                              {cat.group}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {cat.slug}
                            {cat.sortOrder > 0 ? ` · Order: ${cat.sortOrder}` : ""}
                            {cat.description ? ` · ${cat.description.substring(0, 60)}${cat.description.length > 60 ? "..." : ""}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditCategory(cat)}
                          data-testid={`button-edit-category-${cat.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this category? Products in this category will not be deleted but will become uncategorized.")) {
                              deleteCategoryMutation.mutate(cat.id);
                            }
                          }}
                          data-testid={`button-delete-category-${cat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No categories found. Add one to get started.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="purchases" className="mt-6">
          <Dialog open={!!refundOrder} onOpenChange={(open) => { if (!open) { setRefundOrder(null); setRefundInvoiceId(""); setRefundAmount(""); setRefundReason(""); } }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Issue refund</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Refundable balance: <span className="font-medium text-foreground">${Number((refundOrder?.isSubscription ? refundInvoices.find((invoice) => invoice.id === refundInvoiceId)?.refundableAmount : refundOrder?.billing?.refundableAmount) ?? 0).toFixed(2)}</span>. Leave the amount blank to refund the full remaining balance.
                </p>
                {refundOrder?.isSubscription && (
                  <div className="space-y-2">
                    <Label htmlFor="refund-invoice">Subscription payment to refund</Label>
                    {refundInvoicesLoading ? <Skeleton className="h-10 w-full" /> : (
                      <Select value={refundInvoiceId} onValueChange={(value) => { setRefundInvoiceId(value); setRefundAmount(""); }}>
                        <SelectTrigger id="refund-invoice"><SelectValue placeholder="Select a paid subscription invoice" /></SelectTrigger>
                        <SelectContent>
                          {refundInvoices.map((invoice) => (
                            <SelectItem key={invoice.id} value={invoice.id} disabled={!invoice.canRefund}>
                              {invoice.number || invoice.id} · ${invoice.amountPaid.toFixed(2)} · {invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : "Unknown date"}
                              {invoice.canRefund
                                ? ""
                                : invoice.amountPaid > 0 && invoice.amountRefunded >= invoice.amountPaid
                                  ? " · Fully refunded"
                                  : invoice.paymentStatus !== "succeeded"
                                    ? " · Payment unavailable"
                                    : " · Not refundable"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!refundInvoicesLoading && refundInvoices.length === 0 && <p className="text-xs text-muted-foreground">No paid subscription invoices were found.</p>}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="refund-amount">Refund amount (USD)</Label>
                  <Input id="refund-amount" type="number" min="0.01" step="0.01" max={refundOrder?.isSubscription ? refundInvoices.find((invoice) => invoice.id === refundInvoiceId)?.refundableAmount : refundOrder?.billing?.refundableAmount} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} placeholder={`Full: ${Number((refundOrder?.isSubscription ? refundInvoices.find((invoice) => invoice.id === refundInvoiceId)?.refundableAmount : refundOrder?.billing?.refundableAmount) ?? 0).toFixed(2)}`} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refund-reason">Reason (optional)</Label>
                  <Textarea id="refund-reason" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Internal reason for this refund" rows={3} />
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400">This sends money back to the customer and cannot be undone.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setRefundOrder(null)}>Cancel</Button>
                  <Button variant="destructive" disabled={refundMutation.isPending || (refundOrder?.isSubscription && (!refundInvoiceId || refundInvoicesLoading)) || (!!refundAmount && (Number(refundAmount) <= 0 || Number(refundAmount) > Number((refundOrder?.isSubscription ? refundInvoices.find((invoice) => invoice.id === refundInvoiceId)?.refundableAmount : refundOrder?.billing?.refundableAmount) ?? 0)))} onClick={() => refundOrder && refundMutation.mutate({ orderId: refundOrder.id, amount: refundAmount ? Number(refundAmount) : undefined, reason: refundReason.trim() || undefined, invoiceId: refundInvoiceId || undefined })}>
                    {refundMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Issue refund
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={!!cancelOrder} onOpenChange={(open) => !open && setCancelOrder(null)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Cancel subscription</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Choose when this customer’s recurring subscription should end.</p>
                <div className="space-y-2" role="radiogroup" aria-label="Cancellation timing">
                  <label className="flex gap-3 rounded-md border p-3 cursor-pointer">
                    <input type="radio" name="cancel-mode" checked={cancelMode === "period_end"} onChange={() => setCancelMode("period_end")} />
                    <span><span className="font-medium block">At period end</span><span className="text-xs text-muted-foreground">Keeps access through {cancelOrder?.billing?.currentPeriodEnd ? new Date(cancelOrder.billing.currentPeriodEnd).toLocaleDateString() : "the current paid period"}.</span></span>
                  </label>
                  <label className="flex gap-3 rounded-md border p-3 cursor-pointer">
                    <input type="radio" name="cancel-mode" checked={cancelMode === "immediate"} onChange={() => setCancelMode("immediate")} />
                    <span><span className="font-medium block">Immediately</span><span className="text-xs text-muted-foreground">Ends recurring billing and customer access now. This does not issue a refund.</span></span>
                  </label>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-400">Cancellation stops future renewals. Use the refund action separately if money should be returned.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCancelOrder(null)}>Keep subscription</Button>
                  <Button variant="destructive" disabled={cancelSubscriptionMutation.isPending} onClick={() => cancelOrder && cancelSubscriptionMutation.mutate({ orderId: cancelOrder.id, mode: cancelMode })}>
                    {cancelSubscriptionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm cancellation
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <h2 className="text-xl font-semibold">All Purchases</h2>
            {adminOrders && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span data-testid="text-total-orders">{adminOrders.length} orders</span>
                <span data-testid="text-total-revenue">
                  ${adminOrders.reduce((sum, o) => sum + Number(o.total), 0).toFixed(2)} total revenue
                </span>
              </div>
            )}
          </div>

          {ordersLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : adminOrders && adminOrders.length > 0 ? (
            <div className="space-y-3">
              {adminOrders.map((order) => (
                <Card key={order.id} data-testid={`card-order-${order.id}`}>
                  <CardContent className="p-4">
                    <div
                      className="flex items-center justify-between gap-4 flex-wrap cursor-pointer"
                      onClick={() => handleExpandOrder(order.id)}
                      data-testid={`button-toggle-order-${order.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0 flex-wrap">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 flex-shrink-0">
                          <ShoppingCart className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold" data-testid={`text-order-id-${order.id}`}>
                              {order.isSubscription ? `Subscription #${order.id}` : `Order #${order.id}`}
                            </h3>
                            <Badge
                              variant={order.isSubscription ? "outline" : order.status === "completed" ? "default" : "secondary"}
                              className="no-default-active-elevate"
                              data-testid={`badge-order-status-${order.id}`}
                            >
                              {order.isSubscription ? "Subscription" : order.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground" data-testid={`text-order-meta-${order.id}`}>
                            {order.email} &middot; ${order.total} &middot;{" "}
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "Unknown date"}
                            {order.isSubscription
                              ? ` · ${order.subscriptionPlan?.name || "Subscription plan"}`
                              : order.items.length > 0 && ` · ${order.items.length} item${order.items.length > 1 ? "s" : ""}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {(adminUnreadCounts as Record<number, number>)[order.id] > 0 && (
                          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
                            {(adminUnreadCounts as Record<number, number>)[order.id]}
                          </span>
                        )}
                        <Button variant="ghost" size="icon" data-testid={`button-expand-order-${order.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {expandedOrderId === order.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground block">User ID</span>
                            <span className="font-mono text-xs break-all" data-testid={`text-order-userid-${order.id}`}>
                              {order.userId || "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block">Access Code</span>
                            <span className="font-mono text-xs" data-testid={`text-order-access-${order.id}`}>
                              {order.accessCode}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block">Stripe Session</span>
                            <span className="font-mono text-xs break-all" data-testid={`text-order-stripe-${order.id}`}>
                              {order.stripeSessionId ? order.stripeSessionId.substring(0, 20) + "..." : "N/A"}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block">Total</span>
                            <span className="font-semibold" data-testid={`text-order-total-${order.id}`}>
                              ${order.total}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-md border p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <h4 className="text-sm font-medium">Billing</h4>
                            <div className="flex gap-2 flex-wrap">
                              <Badge variant="outline">{order.billing?.paymentStatus || "Payment status unavailable"}</Badge>
                              {order.isSubscription && <Badge variant="secondary">{order.billing?.subscriptionStatus || "Subscription status unavailable"}</Badge>}
                            </div>
                          </div>
                          {order.billing ? (
                            <>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div><span className="block text-xs text-muted-foreground">Paid</span>${Number(order.billing.amountPaid).toFixed(2)}</div>
                                <div><span className="block text-xs text-muted-foreground">Refunded</span>${Number(order.billing.amountRefunded).toFixed(2)}</div>
                                <div><span className="block text-xs text-muted-foreground">Refundable</span>${Number(order.billing.refundableAmount).toFixed(2)}</div>
                                {order.isSubscription && <div><span className="block text-xs text-muted-foreground">{order.billing.cancelAtPeriodEnd ? "Ends" : "Current period ends"}</span>{order.billing.currentPeriodEnd ? new Date(order.billing.currentPeriodEnd).toLocaleDateString() : "—"}</div>}
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                 <Button size="sm" variant="outline" disabled={!order.billing.canRefund && !order.isSubscription} title={!order.billing.canRefund && !order.isSubscription ? "No refundable payment balance is available." : undefined} onClick={(e) => { e.stopPropagation(); setRefundInvoiceId(""); setRefundAmount(""); setRefundOrder(order); }}>Refund</Button>
                                {order.isSubscription && <Button size="sm" variant="outline" disabled={!order.billing.canCancel} title={!order.billing.canCancel ? "This subscription cannot be cancelled in its current state." : undefined} onClick={(e) => { e.stopPropagation(); setCancelMode(order.billing?.cancelAtPeriodEnd ? "immediate" : "period_end"); setCancelOrder(order); }}>Cancel subscription</Button>}
                              </div>
                              {!order.billing.canRefund && <p className="text-xs text-muted-foreground">Refund unavailable: no refundable payment balance.</p>}
                              {order.isSubscription && !order.billing.canCancel && <p className="text-xs text-muted-foreground">Cancellation unavailable: this subscription is not eligible for cancellation.</p>}
                              {order.billing.stripeError && <p className="text-xs text-destructive">Stripe details unavailable: {order.billing.stripeError}</p>}
                              {order.billingEvents && order.billingEvents.length > 0 && (
                                <div className="pt-2 border-t">
                                  <h5 className="text-xs font-medium mb-2">Billing history</h5>
                                  <ol className="space-y-2">
                                    {[...order.billingEvents].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((event) => (
                                      <li key={event.id} className="flex justify-between gap-3 text-xs">
                                        <span><span className="font-medium">{event.eventType}</span> · {event.status}{event.details ? ` · ${event.details}` : ""}</span>
                                        <span className="text-muted-foreground whitespace-nowrap">{event.amount !== null ? `$${Number(event.amount).toFixed(2)} · ` : ""}{new Date(event.createdAt).toLocaleString()}</span>
                                      </li>
                                    ))}
                                  </ol>
                                </div>
                              )}
                            </>
                          ) : <p className="text-sm text-muted-foreground">Billing details are unavailable for this purchase.</p>}
                        </div>

                         <div className="space-y-2">
                          <h4 className="text-sm font-medium flex items-center gap-2">
                             {order.isSubscription ? <CreditCard className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                             {order.isSubscription ? "Subscription" : "Items"}
                          </h4>
                           {order.isSubscription ? (
                             <div className="flex items-center gap-3 p-3 rounded-md bg-muted/50">
                               <CreditCard className="w-5 h-5 text-primary" />
                               <div className="flex-1 min-w-0">
                                 <span className="text-sm font-medium">
                                   {order.subscriptionPlan?.name || "Subscription plan"}
                                 </span>
                                 <span className="block text-xs text-muted-foreground">
                                   Recurring subscription · {order.status}
                                 </span>
                               </div>
                               {order.subscriptionId && (
                                 <span className="font-mono text-xs text-muted-foreground">
                                   {order.subscriptionId}
                                 </span>
                               )}
                             </div>
                           ) : order.items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-3 p-2 rounded-md bg-muted/50"
                              data-testid={`row-order-item-${item.id}`}
                            >
                              {item.product?.image && (
                                <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0">
                                  <img
                                    src={item.product.image}
                                    alt={item.product.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium">
                                  {item.product?.name || `Product #${item.productId}`}
                                </span>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Qty: {item.quantity}
                              </div>
                              <div className="text-sm font-medium">
                                ${item.price}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="pt-3 border-t mt-2">
                          <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                            <Mail className="w-4 h-4" />
                            Messages with {order.email}
                          </h4>
                          {expandedOrderMessages.length > 0 ? (
                            <div className="space-y-2 max-h-72 overflow-y-auto mb-3 pr-1">
                              {expandedOrderMessages.map((msg) => (
                                <div key={msg.id} className={`flex ${msg.senderType === "admin" ? "justify-end" : "justify-start"}`}>
                                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${msg.senderType === "admin" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground border"}`}>
                                    <p className="whitespace-pre-wrap">{msg.body}</p>
                                    <p className={`text-[10px] mt-1 ${msg.senderType === "admin" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                                      {msg.senderType === "admin" ? "You" : "Customer"} · {new Date(msg.createdAt!).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground mb-3">No messages yet. Send one below.</p>
                          )}
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Type a message to the customer... (Ctrl+Enter to send)"
                              rows={2}
                              className="flex-1 resize-none text-sm"
                              value={adminReplyBody}
                              onChange={(e) => setAdminReplyBody(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && adminReplyBody.trim()) {
                                  sendAdminReplyMutation.mutate({ orderId: order.id, body: adminReplyBody.trim() });
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              className="self-end"
                              disabled={!adminReplyBody.trim() || sendAdminReplyMutation.isPending}
                              onClick={() => sendAdminReplyMutation.mutate({ orderId: order.id, body: adminReplyBody.trim() })}
                            >
                              {sendAdminReplyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            </Button>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-1">Customer will receive an email notification</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No purchases yet.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="emails" className="mt-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Email Templates</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Customize the content of automatic emails sent to customers. Use{" "}
              <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{"{{variableName}}"}</code>{" "}
              placeholders where shown.
            </p>
          </div>

          {emailTemplatesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="h-5 bg-muted rounded w-1/3 mb-2 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-2/3 animate-pulse" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {(emailTemplatesList ?? []).map((tpl) => {
                const isEditing = editingTemplateKey === tpl.key;
                const vars: { name: string; description: string }[] = tpl.variables
                  ? JSON.parse(tpl.variables)
                  : [];
                const hasSubject = tpl.key !== "email_footer";

                return (
                  <Card key={tpl.key} className={isEditing ? "border-primary/50" : ""}>
                    <CardContent className="p-6">
                      {isEditing ? (
                        <div className="space-y-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="font-semibold">{tpl.name}</h3>
                              <p className="text-sm text-muted-foreground">{tpl.description}</p>
                            </div>
                            <div className="flex gap-2 shrink-0">
                              <Button
                                size="sm"
                                disabled={updateEmailTemplateMutation.isPending}
                                onClick={() =>
                                  updateEmailTemplateMutation.mutate({
                                    key: tpl.key,
                                    subject: templateEditForm.subject,
                                    body: templateEditForm.body,
                                  })
                                }
                              >
                                {updateEmailTemplateMutation.isPending ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : null}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingTemplateKey(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>

                          {hasSubject && (
                            <div className="space-y-1.5">
                              <label className="text-sm font-medium">Subject line</label>
                              <Input
                                value={templateEditForm.subject}
                                onChange={(e) =>
                                  setTemplateEditForm((f) => ({ ...f, subject: e.target.value }))
                                }
                                placeholder="Email subject"
                              />
                            </div>
                          )}

                          <div className="space-y-1.5">
                            <label className="text-sm font-medium">Body</label>
                            <Textarea
                              rows={9}
                              value={templateEditForm.body}
                              onChange={(e) =>
                                setTemplateEditForm((f) => ({ ...f, body: e.target.value }))
                              }
                              placeholder="Email body text…"
                              className="font-mono text-sm resize-y"
                            />
                            <p className="text-xs text-muted-foreground">
                              Use blank lines to separate paragraphs. HTML is not supported.
                            </p>
                          </div>

                          {vars.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Available placeholders
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {vars.map((v) => (
                                  <button
                                    key={v.name}
                                    type="button"
                                    title={v.description}
                                    className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs font-mono hover:bg-primary/20 transition-colors"
                                    onClick={() => {
                                      const tag = `{{${v.name}}}`;
                                      setTemplateEditForm((f) => ({ ...f, body: f.body + tag }));
                                    }}
                                  >
                                    {`{{${v.name}}}`}
                                    <span className="text-muted-foreground font-sans not-italic ml-0.5">
                                      — {v.description}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1 min-w-0">
                            <h3 className="font-semibold">{tpl.name}</h3>
                            <p className="text-sm text-muted-foreground">{tpl.description}</p>
                            {hasSubject && (
                              <p className="text-xs text-muted-foreground mt-2 font-mono truncate">
                                Subject:{" "}
                                <span className="text-foreground/70">{tpl.subject}</span>
                              </p>
                            )}
                            {tpl.updatedAt && (
                              <p className="text-xs text-muted-foreground">
                                Last saved:{" "}
                                {new Date(tpl.updatedAt).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => {
                              setEditingTemplateKey(tpl.key);
                              setTemplateEditForm({ subject: tpl.subject, body: tpl.body });
                            }}
                          >
                            <Pencil className="h-3 w-3 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">Subscription Plans</h2>
                <Dialog>
                  <DialogTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground transition-colors" title="How subscriptions work">
                      <HelpCircle className="w-4 h-4" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <HelpCircle className="w-5 h-5 text-primary" />
                        How Subscriptions Work
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm mt-2">

                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">1</div>
                          <div>
                            <p className="font-medium">Customer subscribes via Stripe Checkout</p>
                            <p className="text-muted-foreground text-xs mt-0.5">They select a plan and pay monthly or yearly. Stripe handles billing, retries, and receipts automatically.</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">2</div>
                          <div>
                            <p className="font-medium">Customer links their Sierra Chart account name</p>
                            <p className="text-muted-foreground text-xs mt-0.5">After payment they're prompted to enter the name shown under <strong>Help → About</strong> in Sierra Chart ("Registered To:"). This can also be set or changed later in Account Settings.</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">3</div>
                          <div>
                            <p className="font-medium">Sierra Chart access is activated automatically</p>
                            <p className="text-muted-foreground text-xs mt-0.5">Our system calls Sierra Chart's API to add the customer to <strong>SierraChartPro</strong> with an expiry date set to their billing period end + 1 day buffer. Sierra Chart enforces access inside their software.</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">4</div>
                          <div>
                            <p className="font-medium">Access renews automatically each billing cycle</p>
                            <p className="text-muted-foreground text-xs mt-0.5">When Stripe processes a renewal payment, the Sierra Chart expiry date is automatically extended. No manual action needed.</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">5</div>
                          <div>
                            <p className="font-medium">Access is revoked on cancellation or refund</p>
                            <p className="text-muted-foreground text-xs mt-0.5">If a subscription is cancelled, expires, or a full refund is issued, Sierra Chart access is removed immediately. Disputes automatically trigger a suspension.</p>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      <div>
                        <p className="font-medium mb-2">Key behaviors</p>
                        <ul className="space-y-1.5 text-xs text-muted-foreground">
                          <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />Failed payments keep access during Stripe's retry window. Access is only removed if Stripe ultimately cancels the subscription after all retries fail.</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />A background reconciliation job runs hourly to catch any sync failures and retry them automatically.</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />Customers can change their Sierra Chart account name in Account Settings — old access is removed and the new account is granted in one step.</li>
                          <li className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />Admins can manually sync, suspend, or restore any customer's Sierra Chart access via the admin API routes.</li>
                        </ul>
                      </div>

                      <Separator />

                      <p className="text-xs text-muted-foreground">
                        <strong>Plan tiers:</strong> <code className="bg-muted px-1 py-0.5 rounded text-xs">indicators</code> = Indicators Plan; <code className="bg-muted px-1 py-0.5 rounded text-xs">indicators_orderflow</code> = Pro Complete Plan. The tier controls which download files are shown to the customer on their Purchases page.
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Create plans customers can subscribe to. Each plan has a monthly price, a tier, and downloadable files.
              </p>
            </div>
            <Dialog
              open={planDialogOpen}
              onOpenChange={(open) => {
                setPlanDialogOpen(open);
                if (!open) { setEditingPlan(null); setPlanForm(defaultPlanForm); }
              }}
            >
              <DialogTrigger asChild>
                <Button data-testid="button-add-plan" onClick={() => { setEditingPlan(null); setPlanForm(defaultPlanForm); setPlanDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingPlan ? "Edit Plan" : "Add New Plan"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Plan Name</Label>
                    <Input
                      value={planForm.name}
                      onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                      placeholder="e.g. Indicators Plan"
                      data-testid="input-plan-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={planForm.description}
                      onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                      placeholder="What subscribers get access to…"
                      data-testid="input-plan-description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Monthly Price (USD)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={planForm.price}
                        onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
                        placeholder="8.99"
                        data-testid="input-plan-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tier</Label>
                      <Select
                        value={planForm.tier}
                        onValueChange={(val) => setPlanForm({ ...planForm, tier: val })}
                      >
                        <SelectTrigger data-testid="select-plan-tier">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="indicators">Indicators Only</SelectItem>
                          <SelectItem value="indicators_orderflow">Indicators + Order Flow</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    <Label>Plan Features</Label>
                    <div className="space-y-2">
                      {planForm.features.map((feat, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="flex-1 flex items-center gap-2 p-2 rounded-md bg-muted/50">
                            <Check className="w-3.5 h-3.5 flex-shrink-0 text-green-500" />
                            <span className="text-sm">{feat}</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setPlanForm((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== idx) }))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Input
                          value={featureInput}
                          onChange={(e) => setFeatureInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (featureInput.trim()) {
                                setPlanForm((prev) => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
                                setFeatureInput("");
                              }
                            }
                          }}
                          placeholder="e.g. VWAP with dynamic bands"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (featureInput.trim()) {
                              setPlanForm((prev) => ({ ...prev, features: [...prev.features, featureInput.trim()] }));
                              setFeatureInput("");
                            }
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Features shown on the pricing cards. Press Enter or click + to add.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Download Files</Label>
                    <div className="space-y-2">
                      {planForm.downloadFiles.map((df, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0 flex items-center gap-2 p-2 rounded-md bg-muted/50">
                            <FileIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                            <span className="text-sm truncate">{df.name}</span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setPlanForm((prev) => ({ ...prev, downloadFiles: prev.downloadFiles.filter((_, i) => i !== idx) }))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <input
                        ref={planFileRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handlePlanFileUpload(file);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => planFileRef.current?.click()}
                        data-testid="button-upload-plan-file"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload File
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Files subscribers can download after subscribing.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sort Order</Label>
                      <Input
                        type="number"
                        value={planForm.sortOrder}
                        onChange={(e) => setPlanForm({ ...planForm, sortOrder: parseInt(e.target.value) || 0 })}
                        data-testid="input-plan-sort-order"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <Switch
                        checked={planForm.isActive}
                        onCheckedChange={(checked) => setPlanForm({ ...planForm, isActive: checked })}
                        data-testid="switch-plan-active"
                      />
                      <Label>Active</Label>
                    </div>
                  </div>
                  {editingPlan?.stripePriceId && (
                    <p className="text-xs text-muted-foreground">
                      Stripe Price ID: <code className="font-mono">{editingPlan.stripePriceId}</code>
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-2 pt-4 flex-wrap">
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                      onClick={handlePlanSubmit}
                      disabled={createPlanMutation.isPending || updatePlanMutation.isPending}
                      data-testid="button-save-plan"
                    >
                      {(createPlanMutation.isPending || updatePlanMutation.isPending) && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {editingPlan ? "Update Plan" : "Create Plan"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {plansLoading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : subscriptionPlans && subscriptionPlans.length > 0 ? (
            <div className="space-y-3">
              {subscriptionPlans.map((plan) => (
                <Card key={plan.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold">{plan.name}</h3>
                          <Badge variant={plan.isActive ? "default" : "outline"} className="no-default-active-elevate text-xs">
                            {plan.isActive ? "Active" : "Inactive"}
                          </Badge>
                          <Badge variant="secondary" className="no-default-active-elevate text-xs">
                            {plan.tier === "indicators_orderflow" ? "Indicators + Order Flow" : "Indicators Only"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          ${Number(plan.price).toFixed(2)}/mo
                          {plan.downloadFiles && plan.downloadFiles.length > 0
                            ? ` · ${plan.downloadFiles.length} file${plan.downloadFiles.length !== 1 ? "s" : ""}`
                            : " · No files yet"}
                          {!plan.stripePriceId && " · ⚠ No Stripe price set"}
                        </p>
                        {plan.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{plan.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => openEditPlan(plan)}
                          data-testid={`button-edit-plan-${plan.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            if (window.confirm("Delete this subscription plan?")) deletePlanMutation.mutate(plan.id);
                          }}
                          data-testid={`button-delete-plan-${plan.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No subscription plans yet. Add one to start selling subscriptions.</p>
              </CardContent>
            </Card>
          )}

          <Separator className="my-8" />
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h2 className="text-xl font-semibold">Discount Codes</h2>
              <p className="text-sm text-muted-foreground mt-1">Create checkout discounts for one-time purchases and subscriptions. Subscription discounts last for the configured term after redemption.</p>
            </div>
            <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
              <DialogTrigger asChild><Button onClick={() => setDiscountForm(defaultDiscountForm)}><Plus className="mr-2 h-4 w-4" />Create code</Button></DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Create discount code</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2"><Label htmlFor="discount-code">Code</Label><Input id="discount-code" value={discountForm.code} onChange={(e) => setDiscountForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="WELCOME20" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Discount type</Label><Select value={discountForm.discountType} onValueChange={(value: "percent" | "fixed") => setDiscountForm((f) => ({ ...f, discountType: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed USD</SelectItem></SelectContent></Select></div>
                    <div className="space-y-2"><Label htmlFor="discount-value">Value</Label><Input id="discount-value" type="number" min="0.01" step="0.01" value={discountForm.value} onChange={(e) => setDiscountForm((f) => ({ ...f, value: e.target.value }))} placeholder={discountForm.discountType === "percent" ? "20" : "10.00"} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label htmlFor="discount-duration">Duration</Label><Input id="discount-duration" type="number" min="1" step="1" value={discountForm.durationCount} onChange={(e) => setDiscountForm((f) => ({ ...f, durationCount: e.target.value }))} /></div>
                    <div className="space-y-2"><Label>Unit</Label><Select value={discountForm.durationUnit} onValueChange={(value: "months" | "years") => setDiscountForm((f) => ({ ...f, durationUnit: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="months">Months</SelectItem><SelectItem value="years">Years</SelectItem></SelectContent></Select></div>
                  </div>
                  <div className="space-y-2"><Label htmlFor="discount-expires">Use-by date (optional)</Label><Input id="discount-expires" type="date" value={discountForm.expiresAt} onChange={(e) => setDiscountForm((f) => ({ ...f, expiresAt: e.target.value }))} /></div>
                  <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDiscountDialogOpen(false)}>Cancel</Button><Button disabled={createDiscountCodeMutation.isPending || !discountForm.code.trim() || Number(discountForm.value) <= 0 || Number(discountForm.durationCount) < 1 || (discountForm.discountType === "percent" && Number(discountForm.value) > 100)} onClick={() => createDiscountCodeMutation.mutate()}>{createDiscountCodeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create code</Button></div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {discountCodesLoading ? <Skeleton className="h-24 w-full" /> : discountCodes.length ? (
            <div className="space-y-3">
              {discountCodes.map((discount) => (
                <Card key={discount.id}><CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap"><code className="font-semibold">{discount.code}</code><Badge variant={discount.active ? "default" : "outline"}>{discount.active ? "Active" : "Inactive"}</Badge></div>
                    <p className="text-sm text-muted-foreground mt-1">{discount.discountType === "percent" ? `${discount.value}% off` : `$${Number(discount.value).toFixed(2)} off`} · {discount.durationCount} {discount.durationUnit}{discount.expiresAt ? ` · Use by ${new Date(discount.expiresAt).toLocaleDateString()}` : " · No use-by date"}</p>
                  </div>
                  <Button size="sm" variant="outline" disabled={toggleDiscountCodeMutation.isPending} onClick={() => toggleDiscountCodeMutation.mutate({ id: discount.id, active: !discount.active })}>{discount.active ? "Deactivate" : "Activate"}</Button>
                </CardContent></Card>
              ))}
            </div>
          ) : <Card><CardContent className="p-6 text-sm text-muted-foreground">No discount codes yet.</CardContent></Card>}
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Site Settings</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Configure sitewide content that appears across the store.
            </p>
          </div>

          <div className="space-y-4 max-w-2xl">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-semibold">Footer Disclaimer</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Optional text shown in the site footer above the copyright line — useful for legal disclaimers, affiliate disclosures, or trademark notices.
                  </p>
                </div>
                <div className="space-y-2">
                  <Textarea
                    rows={5}
                    value={disclaimerEdit ?? siteSettings?.footer_disclaimer ?? ""}
                    onChange={(e) => setDisclaimerEdit(e.target.value)}
                    placeholder="e.g. Trading involves risk of loss. Past performance is not indicative of future results…"
                    className="resize-y"
                    data-testid="input-footer-disclaimer"
                  />
                  <p className="text-xs text-muted-foreground">
                    Plain text only. Leave blank to show nothing.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={saveSiteSettingMutation.isPending}
                    onClick={() =>
                      saveSiteSettingMutation.mutate({
                        key: "footer_disclaimer",
                        value: disclaimerEdit ?? siteSettings?.footer_disclaimer ?? "",
                      })
                    }
                  >
                    {saveSiteSettingMutation.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Save
                  </Button>
                  {disclaimerEdit !== null && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDisclaimerEdit(null)}
                    >
                      Discard
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
